import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import { charterDestinationsService } from "../../services/charter-destinations.service";
import {
  CharterDestination,
  CreateCharterDestinationDto,
  LocationOption,
} from "../../interfaces/charter-destination.interface";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { useConfirm } from "../../hooks/useConfirm";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateCharterDestinationDto = {
  name: "",
  thumbnailUrl: "",
  locationId: "",
  isVisible: true,
  sortOrder: 0,
};

function getErrorMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as any).response?.data;
    if (r?.message) return Array.isArray(r.message) ? r.message.join(", ") : r.message;
  }
  return fallback;
}

// ─── Thumbnail picker (local preview only — uploaded on form submit) ──────────

interface ThumbnailPickerProps {
  existingUrl: string;
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
}

const ThumbnailPicker = ({ existingUrl, onFileSelect, selectedFile }: ThumbnailPickerProps) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrl = selectedFile ? URL.createObjectURL(selectedFile) : existingUrl;

  return (
    <div className="flex items-center gap-3">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="h-16 w-16 rounded object-cover border border-gray-200 dark:border-gray-600 shrink-0"
        />
      ) : (
        <div className="h-16 w-16 rounded bg-gray-100 dark:border-gray-700 border border-gray-200 dark:bg-gray-700 shrink-0" />
      )}
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          {selectedFile ? "Change image" : existingUrl ? "Replace image" : "Upload image"}
        </button>
        {selectedFile && (
          <button
            type="button"
            onClick={() => onFileSelect(null)}
            className="px-3 py-1.5 text-xs text-red-500 hover:text-red-600"
          >
            Remove
          </button>
        )}
        {selectedFile && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[160px]">
            {selectedFile.name}
          </span>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onFileSelect(e.target.files?.[0] ?? null)}
      />
    </div>
  );
};

// ─── Form ─────────────────────────────────────────────────────────────────────

interface DestinationFormProps {
  form: CreateCharterDestinationDto;
  setF: (patch: Partial<CreateCharterDestinationDto>) => void;
  thumbnailFile: File | null;
  onThumbnailSelect: (file: File | null) => void;
  locations: LocationOption[];
  saving: boolean;
  error: string;
  onSubmit: () => void;
  onCancel: () => void;
  isEdit: boolean;
}

const DestinationForm = ({
  form,
  setF,
  thumbnailFile,
  onThumbnailSelect,
  locations,
  saving,
  error,
  onSubmit,
  onCancel,
  isEdit,
}: DestinationFormProps) => {
  const { t } = useTranslation("common");

  const inputClass =
    "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <div>
        <label className={labelClass}>{t("charter_destination_name")} *</label>
        <input
          className={inputClass}
          value={form.name}
          onChange={(e) => setF({ name: e.target.value })}
          placeholder={t("charter_destination_name")}
        />
      </div>

      <div>
        <label className={labelClass}>{t("charter_destination_thumbnail")}</label>
        <ThumbnailPicker
          existingUrl={form.thumbnailUrl ?? ""}
          selectedFile={thumbnailFile}
          onFileSelect={onThumbnailSelect}
        />
      </div>

      <div>
        <label className={labelClass}>{t("charter_destination_location")} *</label>
        <select
          className={inputClass}
          value={form.locationId}
          onChange={(e) => setF({ locationId: e.target.value })}
        >
          <option value="">{t("charter_destination_location")}</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3">
        <input
          id="isVisible"
          type="checkbox"
          checked={form.isVisible ?? true}
          onChange={(e) => setF({ isVisible: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
        <label htmlFor="isVisible" className="text-sm text-gray-700 dark:text-gray-300">
          {t("charter_destination_visibility")}
        </label>
      </div>

      <div>
        <label className={labelClass}>{t("charter_destination_sort_order")}</label>
        <input
          type="number"
          min={0}
          className={inputClass}
          value={form.sortOrder ?? 0}
          onChange={(e) => setF({ sortOrder: parseInt(e.target.value, 10) || 0 })}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSubmit}
          disabled={saving || !form.name || !form.locationId}
        >
          {saving ? t("loading") : isEdit ? t("save_changes") : t("create")}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CharterDestinationsPage() {
  const { t } = useTranslation("common");
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();

  const [destinations, setDestinations] = useState<CharterDestination[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CharterDestination | null>(null);
  const [form, setForm] = useState<CreateCharterDestinationDto>(EMPTY_FORM);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const patchForm = (patch: Partial<CreateCharterDestinationDto>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [res, locs] = await Promise.all([
        charterDestinationsService.list(),
        charterDestinationsService.listLocations(),
      ]);
      setDestinations(res.data);
      setLocations(locs);
    } catch {
      setError(t("error_loading"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setThumbnailFile(null);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (dest: CharterDestination) => {
    setEditing(dest);
    setForm({
      name: dest.name,
      thumbnailUrl: dest.thumbnailUrl ?? "",
      locationId: dest.locationId,
      isVisible: dest.isVisible,
      sortOrder: dest.sortOrder,
    });
    setThumbnailFile(null);
    setFormError("");
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        await charterDestinationsService.update(editing.id, form, thumbnailFile);
      } else {
        await charterDestinationsService.create(form, thumbnailFile);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(getErrorMsg(err, t("error_saving")));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisible = async (dest: CharterDestination) => {
    try {
      await charterDestinationsService.update(dest.id, { isVisible: !dest.isVisible });
      load();
    } catch {
      setError(t("error_saving"));
    }
  };

  const handleDelete = async (dest: CharterDestination) => {
    const confirmed = await confirm(`${t("confirm_delete_destination")}: "${dest.name}"?`);
    if (!confirmed) return;
    try {
      await charterDestinationsService.remove(dest.id);
      load();
    } catch {
      setError(t("error_deleting"));
    }
  };

  return (
    <Layout title={t("popular_destinations")}>
      {ConfirmDialog}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {t("popular_destinations")}
          </h1>
          {hasPermission(Permission.CHARTER_DESTINATIONS_CREATE) && (
            <Button variant="primary" size="sm" onClick={openCreate}>
              + {t("add_destination")}
            </Button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <Card>
          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t("loading")}</div>
          ) : destinations.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t("no_destinations")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                      {t("charter_destination_thumbnail")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                      {t("charter_destination_name")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                      {t("charter_destination_location")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                      {t("charter_destination_visibility")}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">
                      {t("charter_destination_live_listings")}
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {destinations.map((dest) => (
                    <tr key={dest.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        {dest.thumbnailUrl ? (
                          <img
                            src={dest.thumbnailUrl}
                            alt={dest.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded bg-gray-100 dark:bg-gray-700" />
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {dest.name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {dest.location?.name ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        {hasPermission(Permission.CHARTER_DESTINATIONS_UPDATE) ? (
                          <button
                            onClick={() => handleToggleVisible(dest)}
                            className={[
                              "relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors",
                              dest.isVisible ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform mt-0.5",
                                dest.isVisible ? "translate-x-4 ml-0.5" : "translate-x-0.5",
                              ].join(" ")}
                            />
                          </button>
                        ) : (
                          <span className={dest.isVisible ? "text-green-600" : "text-gray-400"}>
                            {dest.isVisible ? t("visible") : t("hidden")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {dest.liveListingCount}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {hasPermission(Permission.CHARTER_DESTINATIONS_UPDATE) && (
                          <Button variant="outline" size="sm" onClick={() => openEdit(dest)}>
                            {t("edit")}
                          </Button>
                        )}
                        {hasPermission(Permission.CHARTER_DESTINATIONS_DELETE) && (
                          <Button variant="danger" size="sm" onClick={() => handleDelete(dest)}>
                            {t("delete")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Dialog
        open={modalOpen}
        title={editing ? t("edit_destination") : t("add_destination")}
        onClose={() => setModalOpen(false)}
      >
        <DestinationForm
          form={form}
          setF={patchForm}
          thumbnailFile={thumbnailFile}
          onThumbnailSelect={setThumbnailFile}
          locations={locations}
          saving={saving}
          error={formError}
          onSubmit={handleSubmit}
          onCancel={() => setModalOpen(false)}
          isEdit={!!editing}
        />
      </Dialog>
    </Layout>
  );
}
